import client from './client';

// ── Types ────────────────────────────────────────────────────────────────────

export interface NotificationDto {
  readonly id: string;
  /**
   * Notification kind, mirrored from the backend's
   * {@code com.aibuild.os.controlplane.notifications.domain.NotificationType}
   * enum. Typed as an open {@code string} (rather than a literal union) so
   * the frontend renders unknown values gracefully when the backend ships a
   * new type. Today only {@code "GENERIC"} is emitted (placeholder) until
   * concrete types are added to the catalogue.
   */
  readonly type: string;
  readonly title: string;
  readonly body: string;
  readonly actionUrl: string | null;
  readonly metadata: Record<string, unknown>;
  readonly readAt: string | null;
  readonly createdAt: string;
}

export interface NotificationPageDto {
  readonly items: readonly NotificationDto[];
  readonly page: number;
  readonly pageSize: number;
  readonly totalElements: number;
  readonly totalPages: number;
  readonly hasNext: boolean;
}

export interface NotificationPreferencesDto {
  readonly emailEnabled: boolean;
  readonly inAppEnabled: boolean;
}

export interface UnreadCountDto {
  readonly count: number;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

const BASE = '/api/v1/notifications';

export async function listNotifications(
  page = 0,
  size = 20,
  unreadOnly = false,
): Promise<NotificationPageDto> {
  const { data } = await client.get<NotificationPageDto>(BASE, {
    params: { page, size, unreadOnly },
  });
  return data;
}

export async function fetchUnreadCount(): Promise<number> {
  const { data } = await client.get<UnreadCountDto>(`${BASE}/unread-count`);
  return data.count;
}

export async function markNotificationRead(id: string): Promise<void> {
  await client.post(`${BASE}/${encodeURIComponent(id)}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await client.post(`${BASE}/read-all`);
}

export async function deleteNotification(id: string): Promise<void> {
  await client.delete(`${BASE}/${encodeURIComponent(id)}`);
}

export async function clearAllNotifications(): Promise<void> {
  await client.delete(BASE);
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferencesDto> {
  const { data } = await client.get<NotificationPreferencesDto>(`${BASE}/preferences`);
  return data;
}

export async function updateNotificationPreferences(
  prefs: NotificationPreferencesDto,
): Promise<NotificationPreferencesDto> {
  const { data } = await client.put<NotificationPreferencesDto>(`${BASE}/preferences`, prefs);
  return data;
}
